// src/components/crm/NoteComposer.js
//
// Compose UI for the new Notes & Media system.
//
// Phase 1.2: text-only compose with visibility toggle, flag block, contextual save.
// Phase 1.4: opt-in follow-up appointment when flagged + attached to a job.
// Phase 1.7 Slice A: photo upload with staging tray + drag-drop.
//
// UX pattern (revised from techNotes "two-button save" — see Decisions Log
// 2026-04-26 in spatchy-notes-media-roadmap):
//   - Visibility chosen up-front via Internal / Customer-facing toggle
//   - Flag UI is visible only when Internal is selected
//   - One contextual save button at the bottom whose label matches the visibility
//
// Writes directly to organizations/{orgId}/notes — no prop-drilled handler from
// the parent. Notification fan-out, publicAttachments computation, and any
// related side-effects are handled by the onNoteCreated Cloud Function.
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. publicAttachments is ALWAYS written as [] from the client. The CF
//      computes the real value server-side. Firestore rules also force this
//      field to empty on client write so a malicious client cannot grant
//      itself portal access.
//   2. addedAt and updatedAt use serverTimestamp() — never client-supplied.
//   3. authorId is the current auth user's id; rules verify this against
//      request.auth.uid so a client cannot impersonate another author.
//   4. When visibility flips to 'customer', flag state is cleared in the same
//      synchronous call as the visibility change so flag fields can never
//      appear in a customer-facing note payload, even momentarily.
//   5. Flag fields are conditionally spread into the payload — they are
//      structurally absent (not just falsy) on customer-facing notes.
//   6. attachedTo is filtered through a strict allowlist of {customer, quote,
//      job, invoice} types and non-empty string IDs before write.
//   7. attachedIndex is derived from the validated attachedTo so the two
//      arrays cannot drift.
//   8. Note text is hard-capped at 5000 characters at input AND again on
//      payload build (belt and suspenders) to bound document size.
//   9. Text is rendered downstream via React's default escaping — no
//      dangerouslySetInnerHTML anywhere.
//   10. isSaving guard prevents double-submit on rapid clicks.
//   11. PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a — Photo uploads use a
//       pre-allocated noteId so storagePath can include it before the note
//       doc exists. Save uses setDoc with that ID. Photos that fail or are
//       removed get best-effort Storage + Firestore cleanup. Component
//       unmount cancels in-flight uploads and cleans up orphans.

import React, { useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  collection, addDoc, doc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { useDropzone } from 'react-dropzone';
import {
  Lock, Eye, Flag, Phone, Wrench, AlertTriangle, MessageSquare, CalendarPlus,
  ImagePlus, X, RefreshCw,
} from 'lucide-react';
import { db } from '../../firebase/config';
import {
  validateFile,
  compressPhoto,
  createNotesMediaDoc,
  uploadPhotoToStorage,
  removeStagedPhoto,
  newMediaId,
  MAX_PHOTOS_PER_NOTE,
} from '../../utils/notesMediaUpload';

const MAX_TEXT_LENGTH = 5000;
const TEXT_COUNTER_THRESHOLD = 4500;

const VALID_ATTACHED_TYPES = ['customer', 'quote', 'job', 'invoice'];

const FLAG_TYPES = [
  { value: 'callback', label: 'Callback Needed', Icon: Phone },
  { value: 'followup', label: 'Follow-up Job',   Icon: Wrench },
  { value: 'issue',    label: 'Issue to Resolve', Icon: AlertTriangle },
  { value: 'fyi',      label: 'General FYI',     Icon: MessageSquare },
];

// r1-2d-nc-forwardref-4f8a2c31 — forwardRef so a parent can drive commit()
// (see the useImperativeHandle after handleSave). Callers that pass no ref
// are unaffected; forwardRef is transparent when ref is undefined.
const NoteComposer = forwardRef(({
  attachedTo,           // [{ type, id }, ...] — what this note will be linked to
  orgId,                // organizations/{orgId}
  currentUserProfile,   // { id, name, role, ... } — current user (id must equal auth uid)
  orgUsers,             // [{ id, name, role }, ...] — all users in this org
  placeholder = 'Add a note...',
  onNoteCreated,        // optional callback(noteId) on successful create
  // r1-1a-notes-nc-props-5e2a8b71 — Seam 1a: allowFlagging gates the flag UI
  // (omitting orgUsers alone leaves a dead-end empty Notify dropdown);
  // onDirtyChange reports unsaved staged-photo/text state for exit warnings.
  allowFlagging = true,
  onDirtyChange,
}, ref) => {
  // --- State ---
  const [noteText, setNoteText] = useState('');
  const [visibility, setVisibility] = useState('internal');
  const [isFlagged, setIsFlagged] = useState(false);
  const [flagType, setFlagType] = useState('callback');
  const [flagTargetUserId, setFlagTargetUserId] = useState('');
  // PHASE 1.4 VERIFIER: r1-d3e7f201
  // Opt-in scheduling of a follow-up appointment. Default OFF — flagging a note
  // is for "see this when you're on the job" context, not "schedule an extra
  // visit." When checked, NoteComposer additionally writes a Job doc with
  // eventType: 'Follow-Up' assigned to the flag target. Only meaningful when
  // the note is flagged AND attachedTo contains a job (so we know which job
  // the follow-up should reference). UI controls suppress the option when the
  // prerequisites aren't met.
  const [scheduleFollowUp, setScheduleFollowUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
  // Photo staging state. Each tile carries enough to drive the UI and to
  // clean itself up on remove/unmount:
  //   { mediaId, file, previewUrl, status, progress, error, storagePath, _cancel }
  // status ∈ 'compressing' | 'uploading' | 'uploaded' | 'failed'
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState(null);

  // Pre-allocated noteId. Stays null until the user adds their first photo,
  // at which point we mint a Firestore doc ref (no write yet) and use its
  // ID to construct the photo storage path. Save then writes the note with
  // setDoc(noteRef, payload) instead of addDoc. Reset to null after a
  // successful save so a follow-up note allocates a fresh ID.
  const [noteId, setNoteId] = useState(null);
  const noteRefRef = useRef(null);

  // Track photos for cleanup on unmount without re-running the effect on
  // every photos change (which would cancel uploads that just started).
  const photosRef = useRef(photos);
  useEffect(() => { photosRef.current = photos; }, [photos]);

  // Cancel in-flight uploads + revoke blob URLs + delete orphans on unmount.
  useEffect(() => {
    return () => {
      const current = photosRef.current;
      current.forEach((p) => {
        if (p.previewUrl) {
          try { URL.revokeObjectURL(p.previewUrl); } catch (_) {}
        }
        if (typeof p._cancel === 'function') {
          try { p._cancel(); } catch (_) {}
        }
        // Best-effort orphan cleanup — fire and forget, no await.
        if (noteRefRef.current && (p.status === 'uploaded' || p.status === 'uploading' || p.status === 'failed')) {
          removeStagedPhoto({
            orgId,
            noteId: noteRefRef.current.id,
            mediaId: p.mediaId,
            hadUploaded: p.status === 'uploaded',
          }).catch(() => {});
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Derived ---
  const trimmedText = noteText.trim();
  const hasText = trimmedText.length > 0;
  const isInternal = visibility === 'internal';

  // PHASE 1.4 VERIFIER: r1-9a82c5f8
  // Find the first attached job (if any) — used as linkedJobId on the
  // follow-up Job doc, and to extract clientId/clientName for the follow-up.
  // The "schedule follow-up" option is only offered when this is non-null,
  // since follow-ups are always tied to a job.
  const primaryAttachedJob = (attachedTo || []).find(
    a => a && a.type === 'job' && typeof a.id === 'string' && a.id.length > 0
  ) || null;

  // PHASE 1.3 VERIFIER: r2-8b1d4f0c
  // All org users are valid flag targets, including the current user.
  // Self-flagging is a legitimate "personal todo / reminder" pattern,
  // particularly useful for solo operators and small teams. This overrides
  // the Phase 1.1 acceptance criterion 'User cannot flag themselves' —
  // changed during Phase 1.3 testing. See spatchy-notes-media-roadmap
  // Decisions Log entry (added when 1.3 closes; bump roadmap to v6).
  const flaggableUsers = orgUsers || [];

  // --- Photo staging ---

  // PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
  // Pipeline per file: validate → mediaId → preview blob → push 'compressing'
  // → compress → notesMedia doc → upload with progress → mark 'uploaded'.
  // Failure at any step transitions the tile to 'failed' with retry.
  const ensureNoteId = () => {
    if (noteRefRef.current) return noteRefRef.current.id;
    const ref = doc(collection(db, `organizations/${orgId}/notes`));
    noteRefRef.current = ref;
    setNoteId(ref.id);
    return ref.id;
  };

  const processOnePhoto = async ({ tile, theNoteId }) => {
    try {
      // 1. Compress
      const compressed = await compressPhoto(tile.file);

      // 2. Create notesMedia doc (uploadStatus: 'pending')
      await createNotesMediaDoc({
        orgId,
        mediaId: tile.mediaId,
        noteId: theNoteId,
        currentUserId: currentUserProfile.id,
      });

      // 3. Transition to uploading + start the upload
      const { promise, cancel } = uploadPhotoToStorage({
        file: compressed,
        orgId,
        noteId: theNoteId,
        mediaId: tile.mediaId,
        onProgress: (progress) => {
          setPhotos(prev => prev.map(p =>
            p.mediaId === tile.mediaId ? { ...p, progress } : p
          ));
        },
      });
      setPhotos(prev => prev.map(p =>
        p.mediaId === tile.mediaId ? { ...p, status: 'uploading', progress: 0, _cancel: cancel } : p
      ));

      const { storagePath } = await promise;

      // 4. Mark uploaded
      setPhotos(prev => prev.map(p =>
        p.mediaId === tile.mediaId
          ? { ...p, status: 'uploaded', progress: 1, storagePath, _cancel: null }
          : p
      ));
    } catch (err) {
      console.error(`NoteComposer: upload failed for ${tile.mediaId}`, err);
      setPhotos(prev => prev.map(p =>
        p.mediaId === tile.mediaId
          ? { ...p, status: 'failed', error: err?.message || 'Upload failed', _cancel: null }
          : p
      ));
    }
  };

  const handleAddFiles = useCallback((rawFiles) => {
    setPhotoError(null);
    if (!orgId || !currentUserProfile?.id) {
      setPhotoError('Cannot add photos right now. Please reload and try again.');
      return;
    }

    // Capacity check — silently truncate to MAX_PHOTOS_PER_NOTE total.
    const currentCount = photos.length;
    const remainingSlots = MAX_PHOTOS_PER_NOTE - currentCount;
    if (remainingSlots <= 0) {
      setPhotoError(`Maximum ${MAX_PHOTOS_PER_NOTE} photos per note.`);
      return;
    }
    const toProcess = Array.from(rawFiles).slice(0, remainingSlots);
    const truncated = rawFiles.length - toProcess.length;

    // Validate each — partial failures are reported but don't block others.
    const validTiles = [];
    const errors = [];
    for (const file of toProcess) {
      try {
        validateFile(file);
        const mediaId = newMediaId();
        const previewUrl = URL.createObjectURL(file);
        validTiles.push({
          mediaId,
          file,
          previewUrl,
          status: 'compressing',
          progress: 0,
          error: null,
          storagePath: null,
          _cancel: null,
        });
      } catch (err) {
        errors.push(`${file.name || 'file'}: ${err.message}`);
      }
    }

    if (truncated > 0) {
      errors.push(`${truncated} ${truncated === 1 ? 'photo' : 'photos'} ignored (max ${MAX_PHOTOS_PER_NOTE} per note).`);
    }
    if (errors.length > 0) {
      setPhotoError(errors.join(' '));
    }

    if (validTiles.length === 0) return;

    // Allocate noteId on first photo if not yet set.
    const theNoteId = ensureNoteId();

    setPhotos(prev => [...prev, ...validTiles]);
    // Kick off the pipeline for each new tile in parallel.
    validTiles.forEach((tile) => {
      processOnePhoto({ tile, theNoteId });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length, orgId, currentUserProfile?.id]);

  const handleRemovePhoto = (mediaId) => {
    const tile = photos.find(p => p.mediaId === mediaId);
    if (!tile) return;

    // Cancel in-flight upload if any.
    if (typeof tile._cancel === 'function') {
      try { tile._cancel(); } catch (_) {}
    }
    if (tile.previewUrl) {
      try { URL.revokeObjectURL(tile.previewUrl); } catch (_) {}
    }

    // Remove from state immediately for snappy UI.
    setPhotos(prev => prev.filter(p => p.mediaId !== mediaId));

    // Best-effort backend cleanup (Storage + notesMedia doc).
    if (noteRefRef.current) {
      removeStagedPhoto({
        orgId,
        noteId: noteRefRef.current.id,
        mediaId,
        hadUploaded: tile.status === 'uploaded',
      }).catch((err) => {
        console.warn(`NoteComposer: cleanup of ${mediaId} failed`, err);
      });
    }
  };

  const handleRetryPhoto = (mediaId) => {
    const tile = photos.find(p => p.mediaId === mediaId);
    if (!tile || tile.status !== 'failed') return;
    setPhotos(prev => prev.map(p =>
      p.mediaId === mediaId ? { ...p, status: 'compressing', error: null, progress: 0 } : p
    ));
    const theNoteId = ensureNoteId();
    processOnePhoto({ tile: { ...tile, status: 'compressing' }, theNoteId });
  };

  // react-dropzone setup. noClick: true → the textarea/composer area doesn't
  // open the file picker on click (only the explicit ImagePlus button does).
  // The whole composer surface is still a drop target.
  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
    isDragActive,
  } = useDropzone({
    onDrop: handleAddFiles,
    noClick: true,
    noKeyboard: true,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.gif'],
    },
    maxSize: 25 * 1024 * 1024,
    disabled: photos.length >= MAX_PHOTOS_PER_NOTE,
  });

  // --- Handlers ---

  // Visibility change clears flag state synchronously to guarantee no flag
  // fields can leak into a customer-facing payload even between renders.
  // Phase 1.4: also clears scheduleFollowUp so a previously-toggled state
  // can't leak into a non-flag save.
  const handleVisibilityChange = (next) => {
    if (next === visibility) return;
    setVisibility(next);
    if (next === 'customer') {
      setIsFlagged(false);
      setFlagType('callback');
      setFlagTargetUserId('');
      setScheduleFollowUp(false);
    }
  };

  const handleTextChange = (e) => {
    // Hard-cap at input layer (server-side cap also applied at payload build).
    setNoteText(e.target.value.slice(0, MAX_TEXT_LENGTH));
  };

  // --- Validation gates ---
  const flagIsComplete = !isFlagged || (flagType && flagTargetUserId);
  // PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
  // All staged photos must be in a terminal-success state before Save unlocks.
  // Failed or in-flight photos block Save — user must retry or remove.
  const photosReady = photos.every(p => p.status === 'uploaded');
  // PHASE 1.7 VERIFIER: r1-17a-web-poo-3c8f4e2a
  // Photo-only notes: allow saving when there are no words but at least one
  // uploaded photo. A site survey with just photos and no caption is a
  // complete thought. The note's text will be saved as an empty string.
  const hasUploadedPhoto = photos.some(p => p.status === 'uploaded');
  const hasContent = hasText || hasUploadedPhoto;
  // r1-1a-notes-nc-dirty-1d6f3a80 — report unsaved-content state to parent.
  // Hold the callback in a ref (refreshed each render) so the effect depends
  // only on hasContent, not on onDirtyChange's function identity.
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    if (onDirtyChangeRef.current) onDirtyChangeRef.current(hasContent);
  }, [hasContent]);
  const canSave =
    hasContent
    && !isSaving
    && !!orgId
    && !!currentUserProfile?.id
    && Array.isArray(attachedTo)
    && (isInternal ? flagIsComplete : true)
    && photosReady;

  // --- Save ---
  const handleSave = async () => {
    if (!canSave) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      // Strictly validate attachedTo before deriving attachedIndex.
      const safeAttachedTo = (attachedTo || []).filter(
        a => a
          && VALID_ATTACHED_TYPES.includes(a.type)
          && typeof a.id === 'string'
          && a.id.length > 0
      );
      const attachedIndex = safeAttachedTo.map(a => `${a.type}:${a.id}`);

      // Resolve target name at save time. Stored as a snapshot for display
      // resilience if the user is later renamed or deactivated.
      let flagTargetName = null;
      if (isInternal && isFlagged) {
        const target = flaggableUsers.find(u => u.id === flagTargetUserId);
        flagTargetName = target?.name || null;
      }

      const willFlag = isInternal && isFlagged;

      // PHASE 1.4 VERIFIER: r1-3e9a82d4
      // scheduleFollowUp is captured into the note payload. The CF onNoteCreated
      // reads this flag and, if true + a job is in attachedTo, creates the
      // follow-up Job doc server-side AND points the notification's entityId
      // at the new follow-up (so clicking the bell opens the follow-up, not
      // the original job). Moving the follow-up write to the CF is the right
      // architecture: one client write (the note), one CF that handles all
      // downstream effects atomically.
      const willScheduleFollowUp = willFlag && scheduleFollowUp && !!primaryAttachedJob;

      // PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
      // Build media[] from the staged photos array (already filtered down to
      // all-uploaded by canSave). thumbnailPath is null on client write —
      // onNoteCreated back-fills it from the notesMedia doc if the Storage
      // trigger already populated it, otherwise the Storage trigger patches
      // note.media[] directly when it completes.
      const media = photos.map(p => ({
        mediaId: p.mediaId,
        type: 'photo',
        storagePath: p.storagePath,
        thumbnailPath: null,
      }));

      const payload = {
        text: trimmedText.slice(0, MAX_TEXT_LENGTH),
        visibility,
        flagged: willFlag,
        // Flag fields are structurally absent on non-flagged or customer-facing notes.
        ...(willFlag ? {
          flagType,
          flagTargetUserId,
          flagTargetName,
          // FO-4 multi-reviewer: every flag must carry flagReviewerIds[] — the
          // inbox/bell queries filter on array-contains(flagReviewerIds). A
          // manually-composed flag has a single target, so the array is that one
          // user. Without this, manual flags are invisible to the new queries.
          flagReviewerIds: [flagTargetUserId],
          // Server uses this to decide whether to create the follow-up Job.
          // Defaults to false; only true when user explicitly opted in.
          scheduleFollowUp: willScheduleFollowUp,
        } : {}),
        resolved: false,
        resolvedAt: null,
        resolvedBy: null,
        isPinned: false,
        authorId: currentUserProfile.id,
        authorName: currentUserProfile.name || 'Unknown',
        addedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        attachedTo: safeAttachedTo,
        attachedIndex,
        // Always [] from client; CF computes the real value. Rules force this.
        publicAttachments: [],
        media,
      };

      // PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
      // If we pre-allocated a noteId (because the user added photos), use
      // setDoc against that specific ID so the photos' storagePath aligns.
      // Otherwise fall through to the original addDoc flow (no photos, no
      // pre-allocation needed).
      let createdNoteId;
      if (noteRefRef.current) {
        await setDoc(noteRefRef.current, payload);
        createdNoteId = noteRefRef.current.id;
      } else {
        const docRef = await addDoc(
          collection(db, `organizations/${orgId}/notes`),
          payload
        );
        createdNoteId = docRef.id;
      }

      // Revoke all preview URLs before clearing state.
      photos.forEach((p) => {
        if (p.previewUrl) {
          try { URL.revokeObjectURL(p.previewUrl); } catch (_) {}
        }
      });

      // Reset on success
      setNoteText('');
      setVisibility('internal');
      setIsFlagged(false);
      setFlagType('callback');
      setFlagTargetUserId('');
      setScheduleFollowUp(false);
      setPhotos([]);
      setPhotoError(null);
      // PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
      // Reset the pre-allocated noteId so the next note allocates a fresh
      // ID — otherwise a subsequent note would overwrite this one.
      setNoteId(null);
      noteRefRef.current = null;

      if (onNoteCreated) onNoteCreated(createdNoteId);
    } catch (err) {
      // Don't clear the form on failure — user shouldn't lose their text or photos.
      console.error('NoteComposer: save failed', err);
      setSaveError('Could not save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // r1-2d-nc-imperative-4f8a2c31 — imperative handle so a parent (QuoteFormModal)
  // can post the staged note as part of ITS save, removing the need for a second
  // click on this composer's own button. commit() delegates to handleSave, which
  // is a clean no-op when empty (canSave false → early return) and never throws
  // (it catches its own errors into saveError). On success handleSave clears the
  // draft as usual.
  //
  // Ref-held-callback pattern, matching r1-1a-notes-nc-dirty-1d6f3a80 above:
  // hold handleSave/hasContent in refs refreshed EVERY render, then give
  // useImperativeHandle an empty dep array. The handle object stays stable
  // (satisfies react-hooks/exhaustive-deps — no lint escape hatch a future
  // reader could "fix" into a stale closure) while the refs keep the closure
  // current. Placed after handleSave so both are in scope.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const hasContentRef = useRef(hasContent);
  hasContentRef.current = hasContent;
  useImperativeHandle(ref, () => ({
    commit: () => handleSaveRef.current(),
    hasContent: () => hasContentRef.current,
  }), []);

  // --- Render ---
  return (
    <div className="space-y-3" {...getRootProps()}>
      {/* Hidden input mounted by react-dropzone for the file picker */}
      <input {...getInputProps()} />

      {/* Visibility toggle — always visible */}
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
        <button
          type="button"
          onClick={() => handleVisibilityChange('internal')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            isInternal
              ? 'bg-white text-slate-700 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Lock size={11} /> Internal
        </button>
        <button
          type="button"
          onClick={() => handleVisibilityChange('customer')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            !isInternal
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Eye size={11} /> Customer-facing
        </button>
      </div>

      {/* Textarea with drag-drop overlay */}
      <div className="relative">
        <textarea
          value={noteText}
          onChange={handleTextChange}
          placeholder={placeholder}
          rows={3}
          maxLength={MAX_TEXT_LENGTH}
          className={`w-full text-sm border rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300 ${
            isDragActive ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200'
          }`}
        />
        {isDragActive && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-indigo-400 bg-indigo-50/80">
            <span className="text-sm font-semibold text-indigo-700">Drop photos to attach</span>
          </div>
        )}
        {noteText.length >= TEXT_COUNTER_THRESHOLD && (
          <p className={`text-xs mt-1 text-right ${
            noteText.length >= MAX_TEXT_LENGTH ? 'text-red-500 font-semibold' : 'text-slate-400'
          }`}>
            {noteText.length} / {MAX_TEXT_LENGTH}
          </p>
        )}
      </div>

      {/* PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
          Photo action row — Add Photos button. Photo error renders inline. */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={openFilePicker}
          disabled={photos.length >= MAX_PHOTOS_PER_NOTE}
          className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 hover:text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ImagePlus size={11} />
          {photos.length === 0 ? 'Add photos' : `Add more (${photos.length}/${MAX_PHOTOS_PER_NOTE})`}
        </button>
        {photoError && (
          <p className="text-xs text-red-600 flex-1 min-w-0">{photoError}</p>
        )}
      </div>

      {/* PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
          Staging tray — only renders when there are photos. */}
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <div
              key={p.mediaId}
              className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200 bg-slate-100 group"
            >
              <img
                src={p.previewUrl}
                alt=""
                className="w-full h-full object-cover"
              />

              {/* Status overlays */}
              {p.status === 'compressing' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <span className="text-[10px] font-semibold text-white">Compressing…</span>
                </div>
              )}
              {p.status === 'uploading' && (
                <>
                  <div className="absolute inset-0 bg-black/20" />
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
                    <div
                      className="h-full bg-indigo-400 transition-all"
                      style={{ width: `${Math.round((p.progress || 0) * 100)}%` }}
                    />
                  </div>
                </>
              )}
              {p.status === 'failed' && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-500/40">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRetryPhoto(p.mediaId); }}
                    className="flex items-center justify-center w-7 h-7 rounded-full bg-white text-red-600 hover:bg-red-50"
                    title={p.error || 'Retry'}
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
              )}

              {/* Remove button — visible on hover always; tap-friendly on mobile */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemovePhoto(p.mediaId); }}
                className="absolute top-0.5 right-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-black/60 text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Flag block — only when Internal AND there's text */}
      {/* r1-1a-notes-flaggate-7c4d9e26 — allowFlagging=false hides all flag UI */}
      {allowFlagging && isInternal && hasText && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setIsFlagged(prev => !prev)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
              isFlagged
                ? 'bg-amber-100 text-amber-700 border-amber-300'
                : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-amber-50 hover:text-amber-600'
            }`}
          >
            <Flag size={10} />
            {isFlagged ? 'Flagged for follow-up' : 'Flag for follow-up'}
          </button>

          {isFlagged && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-2">
              {/* Flag type */}
              <div>
                <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Type</p>
                <div className="grid grid-cols-2 gap-1">
                  {FLAG_TYPES.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFlagType(value)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        flagType === value
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                      }`}
                    >
                      <Icon size={11} /> {label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Notify target */}
              <div>
                <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Notify</p>
                <select
                  value={flagTargetUserId}
                  onChange={(e) => setFlagTargetUserId(e.target.value)}
                  className="w-full text-xs border border-amber-200 rounded-md p-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  <option value="">— Select a person —</option>
                  {flaggableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name}{u.role ? ` (${u.role})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* PHASE 1.4 VERIFIER: r1-2f6c8b94
                  Opt-in "schedule follow-up appointment" checkbox.
                  Only shown when this note is attached to a job (follow-ups
                  need a parent job for linkedJobId). Default OFF. */}
              {primaryAttachedJob && (
                <div className="pt-1 border-t border-amber-200">
                  <label className="flex items-start gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={scheduleFollowUp}
                      onChange={(e) => setScheduleFollowUp(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 text-amber-600 border-amber-300 rounded focus:ring-amber-400 cursor-pointer"
                    />
                    <div className="flex-1">
                      <span className="flex items-center gap-1 text-xs font-semibold text-amber-800">
                        <CalendarPlus size={11} /> Also schedule a follow-up appointment
                      </span>
                      <span className="block text-[10px] text-amber-700 mt-0.5 leading-tight">
                        Creates a follow-up event on the calendar for the selected person, next business day at 9 AM.
                      </span>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PHASE 1.7 VERIFIER: r1-17a-web-poo-3c8f4e2a
          Footer: error message + contextual save button. Visible when there's
          text OR an uploaded photo (photo-only notes are allowed). */}
      {hasContent && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-red-600 flex-1">
            {saveError || (!photosReady && photos.length > 0
              ? 'Waiting for photos to finish uploading…'
              : '')}
          </p>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className={`flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isInternal
                ? (isFlagged
                    ? 'text-white bg-amber-500 hover:bg-amber-600'
                    : 'text-slate-700 bg-slate-100 hover:bg-slate-200')
                : 'text-white bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isInternal
              ? (isFlagged
                  ? <><Flag size={11} /> {isSaving ? 'Saving…' : 'Save & Flag'}</>
                  : <><Lock size={11} /> {isSaving ? 'Saving…' : 'Save Internal'}</>)
              : <><Eye size={11} /> {isSaving ? 'Saving…' : 'Save for Customer'}</>
            }
          </button>
        </div>
      )}
    </div>
  );
});

export default NoteComposer;